import React from 'react';

import {
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { getRegionNameFromCode } from '~/utils/brandRegionOverrides';

import {
  CellProvenance,
  buildCellKey,
  countEnabled,
  keysForAll,
  keysForRegion,
  keysForTier,
  provenanceLabel,
  shortTierLabels,
} from './luxPlusScheduleDefaults';

interface Props {
  regions: ReadonlyArray<string>;
  tiers: ReadonlyArray<string>;
  enabled: Record<string, boolean>;
  provenance: Record<string, CellProvenance>;
  onToggleCell: (region: string, tier: string) => void;
  onToggleRegion: (region: string, nextEnabled: boolean) => void;
  onToggleTier: (tier: string, nextEnabled: boolean) => void;
  onToggleAll: (nextEnabled: boolean) => void;
}

const REGION_COLUMN_WIDTH = 240;
const TIER_COLUMN_WIDTH = 130;
const MATRIX_MIN_WIDTH = 720;

export default function DefaultsMatrix({
  regions,
  tiers,
  enabled,
  provenance,
  onToggleCell,
  onToggleRegion,
  onToggleTier,
  onToggleAll,
}: Props) {
  const shortLabels = shortTierLabels(tiers);
  const allKeys = keysForAll(regions, tiers);
  const allOnCount = countEnabled(allKeys, enabled);

  // stickyHeader needs a height-bounded container, so maxHeight is load-bearing, not cosmetic.
  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: '62vh' }}>
      <Table size="small" stickyHeader sx={{ minWidth: MATRIX_MIN_WIDTH }}>
        <TableHead>
          <TableRow sx={{ '& th': { verticalAlign: 'top' } }}>
            <TableCell sx={{ width: REGION_COLUMN_WIDTH }}>
              <Typography variant="body2" fontWeight={600}>
                Region
              </Typography>
            </TableCell>

            {tiers.map((tier, index) => {
              const onCount = countEnabled(keysForTier(tier, regions), enabled);

              return (
                <TableCell key={tier} align="center" aria-label={tier} sx={{ width: TIER_COLUMN_WIDTH }}>
                  <Tooltip title={tier}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {shortLabels[index]}
                    </Typography>
                  </Tooltip>
                  <Checkbox
                    size="small"
                    checked={onCount === regions.length && regions.length > 0}
                    indeterminate={onCount > 0 && onCount < regions.length}
                    onChange={() => onToggleTier(tier, onCount !== regions.length)}
                    inputProps={{ 'aria-label': `Enable ${tier} for every region` }}
                  />
                </TableCell>
              );
            })}

            <TableCell align="center" sx={{ width: TIER_COLUMN_WIDTH }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                All tiers
              </Typography>
              <Checkbox
                size="small"
                checked={allOnCount === allKeys.length && allKeys.length > 0}
                indeterminate={allOnCount > 0 && allOnCount < allKeys.length}
                onChange={() => onToggleAll(allOnCount !== allKeys.length)}
                inputProps={{ 'aria-label': 'Enable every tier in every region' }}
              />
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {regions.map((region) => {
            const regionName = getRegionNameFromCode(region);
            const onCount = countEnabled(keysForRegion(region, tiers), enabled);

            return (
              <TableRow key={region} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="baseline">
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace', minWidth: 22 }}
                    >
                      {region}
                    </Typography>
                    <Typography component="span" variant="body2">
                      {regionName}
                    </Typography>
                  </Stack>
                </TableCell>

                {tiers.map((tier) => {
                  const key = buildCellKey(region, tier);

                  return (
                    <TableCell key={key} align="center">
                      <Tooltip title={provenanceLabel(provenance[key])}>
                        <Checkbox
                          size="small"
                          checked={!!enabled[key]}
                          onChange={() => onToggleCell(region, tier)}
                          inputProps={{ 'aria-label': `${regionName}, ${tier}` }}
                        />
                      </Tooltip>
                    </TableCell>
                  );
                })}

                <TableCell align="center">
                  <Checkbox
                    size="small"
                    checked={onCount === tiers.length && tiers.length > 0}
                    indeterminate={onCount > 0 && onCount < tiers.length}
                    onChange={() => onToggleRegion(region, onCount !== tiers.length)}
                    inputProps={{ 'aria-label': `Enable every tier for ${regionName}` }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
